import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../utils/api';
import { Mail, ArrowLeft, CheckCircle, Lock, Eye, EyeOff } from 'lucide-react';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  
  const [step, setStep] = useState(resetToken ? 'reset' : 'request');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [token, setToken] = useState(resetToken || '');

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await api.post('/auth/forgot-password', { email });
      
      // For demo purposes, we show the token (in production, this would be sent via email)
      if (response.data.reset_token) {
        setToken(response.data.reset_token);
        toast.success('Reset link generated! Check below for the token.');
        setStep('reset');
      } else {
        toast.success('If an account with this email exists, a reset link has been sent.');
        setStep('sent');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request password reset');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.post('/auth/reset-password', {
        token: token,
        new_password: newPassword
      });
      
      toast.success('Password reset successfully!');
      setStep('success');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const loginPath = tenantSlug ? `/${tenantSlug}/login` : '/login';

  // Success state
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white border-slate-200 shadow-2xl">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
              <h2 className="text-xl font-semibold text-[#0a192f]">Password Reset Complete!</h2>
              <p className="text-[#0a192f]/70">Your password has been reset successfully.</p>
              <Button 
                onClick={() => navigate(loginPath)} 
                className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold shadow-lg shadow-cyan-500/25"
              >
                Go to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Email sent confirmation
  if (step === 'sent') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white border-slate-200 shadow-2xl">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <Mail className="w-16 h-16 text-cyan-500 mx-auto" />
              <h2 className="text-xl font-semibold text-[#0a192f]">Check Your Email</h2>
              <p className="text-[#0a192f]/70">
                If an account exists with that email, we've sent password reset instructions.
              </p>
              <div className="pt-4 space-y-2">
                <Button 
                  variant="outline" 
                  onClick={() => setStep('request')} 
                  className="w-full border-[#0a192f]/30 text-[#0a192f] hover:bg-[#0a192f]/5"
                >
                  Try Again
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate(loginPath)} 
                  className="w-full text-[#0a192f]/70 hover:text-[#0a192f] hover:bg-[#0a192f]/5"
                >
                  Back to Login
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reset password form (with token)
  if (step === 'reset') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white border-slate-200 shadow-2xl">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-lg border border-cyan-500/30">
                <Lock className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <CardTitle className="text-[#0a192f]">Reset Password</CardTitle>
                <CardDescription className="text-[#0a192f]/70">Enter your new password</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              {/* Token field (for demo) */}
              <div className="space-y-2">
                <Label htmlFor="token" className="text-[#0a192f]">Reset Token</Label>
                <Input
                  id="token"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Paste your reset token"
                  required
                  className="bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                  data-testid="reset-token-input"
                />
                <p className="text-xs text-[#0a192f]/50">
                  This token was provided when you requested a password reset
                </p>
              </div>
              
              {/* New Password */}
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-[#0a192f]">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    minLength={8}
                    className="bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0a192f]/50 hover:text-[#0a192f]"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-[#0a192f]/50">Minimum 8 characters</p>
              </div>
              
              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#0a192f]">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  className="bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                  data-testid="confirm-password-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold shadow-lg shadow-cyan-500/25" 
                disabled={loading}
                data-testid="reset-password-submit"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
              
              <Button 
                type="button"
                variant="ghost" 
                onClick={() => navigate(loginPath)} 
                className="w-full text-[#0a192f]/70 hover:text-[#0a192f] hover:bg-[#0a192f]/5"
              >
                Back to Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Request reset form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white border-slate-200 shadow-2xl">
        <CardHeader>
          <Button 
            variant="ghost" 
            onClick={() => navigate(loginPath)}
            className="w-fit -ml-2 mb-2 text-[#0a192f]/70 hover:text-[#0a192f] hover:bg-[#0a192f]/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-lg border border-cyan-500/30">
              <Mail className="w-6 h-6 text-cyan-600" />
            </div>
            <div>
              <CardTitle className="text-[#0a192f]">Forgot Password?</CardTitle>
              <CardDescription className="text-[#0a192f]/70">Enter your email to reset your password</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRequestReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#0a192f]">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20"
                data-testid="forgot-email-input"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold shadow-lg shadow-cyan-500/25" 
              disabled={loading}
              data-testid="forgot-password-submit"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
